import { wrapInRow } from "@/components/email-composer/converter/merge-header";
import type {
  EmailButtonNode,
  EmailParagraphNode,
  EmailTopLevelNode,
} from "@/components/email-composer/types/tiptap-json";

import { isColorDark } from "@/lib/color";

/**
 * The connect link as an email body, for the compose drawer to open on.
 *
 * Separate from `wearable-connect.ts` on purpose: that module is React-free and
 * is imported by the chart's measurement rendering, so the email composer's node
 * types have no business in it. Same split as forms — `form-message-utils` for
 * the text, `form-email-utils` for the document.
 *
 * Header and signature are not built here; the drawer wraps whatever this
 * returns in both.
 */

function makeParagraph(text?: string): EmailParagraphNode {
  if (!text) {
    return { type: "emailParagraph", attrs: { textAlign: "left" } };
  }
  return {
    type: "emailParagraph",
    attrs: { textAlign: "left" },
    content: [{ type: "text", text }],
  };
}

function makeButton(href: string, buttonColor: string): EmailButtonNode {
  return {
    type: "emailButton",
    attrs: {
      text: "Connect my device",
      href,
      backgroundColor: buttonColor,
      textColor: isColorDark(buttonColor) ? "#ffffff" : "#000000",
      alignment: "center",
      borderRadius: "lg",
    },
  } as EmailButtonNode;
}

export function buildConnectLinkEmailBody(
  patientFirstName: string,
  url: string,
  clinicName: string | undefined,
  buttonColor: string,
): EmailTopLevelNode[] {
  const clinic = clinicName || "your care team";
  return [
    wrapInRow(
      makeParagraph(
        `Hi ${patientFirstName}, connect your wearable or health app so your readings reach ${clinic}.`,
      ),
    ),
    wrapInRow(makeParagraph()),
    wrapInRow(makeButton(url, buttonColor)),
    wrapInRow(makeParagraph()),
    // The link in full as well as behind the button: a patient whose mail app
    // opens links in its own embedded browser has to move it to a real one, and
    // the aggregator refuses to authorise anywhere else.
    wrapInRow(
      makeParagraph(`If the button doesn't open, paste this link instead:`),
    ),
    wrapInRow(makeParagraph(url)),
  ];
}
